import React, { useEffect } from "react";
import mapboxgl from "mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import { Box } from "@chakra-ui/react";

mapboxgl.accessToken =
  "pk.eyJ1IjoiamFuc2Fqb3NlIiwiYSI6ImNrbmlmenZ4NjJ1aWoycGxjdG1qbGhwY2wifQ.waif_7e7ELIHRq8HH7OuVw";

interface PlacePrediction {
  placePrediction: {
    placeId: string;
    text: { text: string };
    structuredFormat: {
      mainText: { text: string };
      secondaryText: { text: string };
    };
    types: string[];
    location: string;
    distanceMeters: number;
  };
}

interface PlaceResult {
  place_id: string;
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  types: string[];
}

interface GeocodeResponseProps {
  placeId: string;
  address: string;
  types: string[];
}
interface AutocompleteResponseProps {
  placeId: string;
  text: string;
  types: string[];
  distanceMeters: number;
}
interface Feature {
  type: "Feature";
  properties: GeocodeResponseProps | AutocompleteResponseProps;
  geometry: {
    type: "Point";
    coordinates: [number, number];
  };
}

export interface FeatureCollection {
  type: "FeatureCollection";
  features: Feature[];
}

export function convertAutocompleteResultToGeoJSON(
  suggestions: PlacePrediction[]
): FeatureCollection {
  console.log("Data suggestions:", suggestions);
  const features: Feature[] = [];
  suggestions.forEach((suggestion) => {
    if (
      !suggestion ||
      !suggestion.placePrediction ||
      !suggestion.placePrediction.location
    ) {
      console.log("invalid data");
      return;
    }

    // console.log("Suggestion:", suggestion);
    const coordinatesMatch = suggestion.placePrediction.location.match(
      /POINT \((\d+\.\d+)\s+(\d+\.\d+)\)/
    );
    if (!coordinatesMatch) {
      console.log("Invalid point format");
      return;
    }

    const [longitude, latitude] = coordinatesMatch.slice(1).map(Number);

    features.push({
      type: "Feature",
      properties: {
        placeId: suggestion.placePrediction.placeId,
        text: suggestion.placePrediction.text.text,
        types: suggestion.placePrediction.types,
        distanceMeters: suggestion.placePrediction.distanceMeters,
      },
      geometry: {
        type: "Point",
        coordinates: [longitude, latitude],
      },
    });
  });

  return {
    type: "FeatureCollection",
    features,
  };
}

export function convertGeocodeResultToGeoJSON(
  results: PlaceResult[]
): FeatureCollection {
  const features: Feature[] = [];

  results.forEach((result) => {
    if (!result || !result.geometry || !result.geometry.location) {
      console.log("invalid data");
      return;
    }

    const { lat, lng } = result.geometry.location;

    features.push({
      type: "Feature",
      properties: {
        placeId: result.place_id,
        address: result.formatted_address,
        types: result.types,
      },
      geometry: {
        type: "Point",
        coordinates: [lng, lat],
      },
    });
  });

  return {
    type: "FeatureCollection",
    features,
  };
}

const GeocodeMapComponent = ({
  resultsGeoJSON,
}: {
  resultsGeoJSON: FeatureCollection;
}) => {
  const map = React.useRef(null as any);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);

  const currentMarkers = React.useRef([] as mapboxgl.Marker[]);

  useEffect(() => {
    map.current = new mapboxgl.Map({
      container: "map-container",
      style: "mapbox://styles/mapbox/streets-v11",
      center: [103.93280576975415, 1.30646686490056],
      zoom: 12,
    });

    map.current.on("load", () => setIsMapLoaded(true));
    return () => map.current.remove();
  }, []);

  useEffect(() => {
    if (!isMapLoaded) return;

    // remove the current markers
    currentMarkers.current.forEach((marker) => marker.remove());
    currentMarkers.current = [];

    // Set the centerLon and centerLat to the coordinates of the first result
    if (
      resultsGeoJSON.features.length &&
      resultsGeoJSON.features[0] &&
      resultsGeoJSON.features[0].geometry &&
      resultsGeoJSON.features[0].geometry.coordinates
    ) {
      const [centerLon, centerLat] =
        resultsGeoJSON.features[0].geometry.coordinates;
      map.current.flyTo({ center: [centerLon, centerLat] });
    }

    resultsGeoJSON.features.forEach((feature, index) => {
      const coordinates = feature.geometry.coordinates;

      const markerElement = document.createElement("div");
      markerElement.className = "custom-marker";
      markerElement.innerHTML = `<span>${index + 1}</span>`;

      let popupText = "";
      if ("text" in feature.properties) {
        popupText = (feature.properties as AutocompleteResponseProps).text;
      } else {
        popupText = (feature.properties as GeocodeResponseProps).address;
      }

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
      }).setText(popupText);

      const marker = new mapboxgl.Marker({
        element: markerElement,
      })
        .setLngLat(coordinates)
        .addTo(map.current)
        .setPopup(popup);
      currentMarkers.current.push(marker);

      marker.getElement().addEventListener("mouseenter", () => {
        popup.addTo(map.current);
      });

      marker.getElement().addEventListener("mouseleave", () => {
        popup.remove();
      });
    });
  }, [isMapLoaded, resultsGeoJSON]);

  return (
    <Box
      id="map-container"
      position="relative"
      style={{ width: "100%", height: "40vw" }}
    />
  );
};

export default GeocodeMapComponent;

// Add CSS for the custom marker
const css = `
.custom-marker {
  background-color: white;
  border: 2px solid black;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
}

.custom-marker span {
  color: black;
}
`;

// Append the CSS to the document
const style = document.createElement("style");
style.innerHTML = css;
document.head.appendChild(style);
